import React, { useState } from "react";
import { Upload, X, Loader2, FileText } from "lucide-react";
import api from "../lib/api";
import { toast } from "sonner";

export const CurioImportModal = ({ onClose, onDone }) => {
  const [file, setFile] = useState(null);
  const [overwrite, setOverwrite] = useState(false);
  const [publish, setPublish] = useState(false);
  const [busy, setBusy] = useState(false);
  const [res, setRes] = useState(null);

  const run = async () => {
    if (!file) return;
    setBusy(true);
    const fd = new FormData();
    fd.append("file", file);
    fd.append("overwrite", overwrite);
    fd.append("publish", publish);
    try {
      const r = await api.post("/admin/curios/workbench/import", fd);
      setRes(r.data);
      toast.success(`${r.data.imported} imported, ${r.data.overwritten} overwritten, ${r.data.skipped} skipped`);
      onDone(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Import failed");
    } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" onClick={onClose} data-testid="wb-import-modal">
      <div className="bg-[#FAF7F0] border border-[#D8CEB9] rounded-sm w-full max-w-lg max-h-[85vh] overflow-y-auto p-6" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-start justify-between mb-1">
          <h2 className="font-serif-display text-2xl font-bold text-[#1A1615]">Import Curios from CSV</h2>
          <button onClick={onClose} data-testid="wb-import-close" className="p-1 text-[#7A6C5D] hover:text-[#9E4535]"><X className="w-5 h-5" /></button>
        </div>
        <p className="font-ui text-xs text-[#7A6C5D] mb-4">Columns: slug, title, hook, full_fact, subject_slug, tags (a|b), image_url, image_alt, sources (url1|url2), social_caption. Same format as Export.</p>

        <label className="flex items-center gap-3 border border-dashed border-[#C5A059] rounded-sm p-4 cursor-pointer hover:bg-[#F3EDE0] transition-colors mb-4">
          <FileText className="w-5 h-5 text-[#9E4535]" />
          <span className="font-ui text-sm text-[#4A4036] truncate" data-testid="wb-import-filename">{file ? file.name : "Choose a .csv file"}</span>
          <input type="file" accept=".csv,text/csv,application/vnd.ms-excel,text/plain" className="hidden" data-testid="wb-import-file" onChange={(e) => { setFile(e.target.files?.[0] || null); setRes(null); }} />
        </label>

        <label className="flex items-center gap-2 font-ui text-sm text-[#4A4036] mb-2">
          <input type="checkbox" checked={overwrite} onChange={(e) => setOverwrite(e.target.checked)} data-testid="wb-import-overwrite" />
          Overwrite existing on duplicate slug <span className="text-[11px] text-[#9A8C7D]">(keeps image unless CSV provides one)</span>
        </label>
        <label className="flex items-center gap-2 font-ui text-sm text-[#4A4036] mb-5">
          <input type="checkbox" checked={publish} onChange={(e) => setPublish(e.target.checked)} data-testid="wb-import-publish" />
          Publish immediately <span className="text-[11px] text-[#9A8C7D]">(off = saved as Needs review)</span>
        </label>

        <button onClick={run} disabled={!file || busy} data-testid="wb-import-submit" className="inline-flex items-center gap-2 bg-[#9E4535] text-[#FAF7F0] font-ui text-sm font-semibold px-4 py-2 rounded-sm hover:bg-[#82382B] disabled:opacity-50">
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />} Import
        </button>

        {res && <ImportReport res={res} />}
      </div>
    </div>
  );
};

const ImportReport = ({ res }) => (
  <div className="mt-5 border-t border-[#D8CEB9] pt-4 font-ui text-sm text-[#4A4036]" data-testid="wb-import-report">
    <p className="font-semibold" data-testid="wb-import-summary">
      {res.imported} imported, {res.overwritten} overwritten, {res.skipped} skipped (duplicates){res.errors.length ? `, ${res.errors.length} errors` : ""}
    </p>
    {[["Imported", res.imported_slugs], ["Overwritten", res.overwritten_slugs], ["Skipped", res.skipped_slugs]].map(([l, list]) => list.length > 0 && (
      <div key={l} className="mt-2">
        <p className="font-mono-cv text-[10px] uppercase tracking-wider text-[#7A6C5D]">{l}</p>
        <p className="font-mono-cv text-xs break-words">{list.join(", ")}</p>
      </div>
    ))}
    {res.errors.length > 0 && (
      <div className="mt-2 text-[#8A1C1C]" data-testid="wb-import-errors">
        {res.errors.map((e, i) => <p key={i} className="text-xs">Row {e.row} ({e.slug}): {e.error}</p>)}
      </div>
    )}
    {res.warnings?.length > 0 && (
      <div className="mt-2 text-[#8A5A00]" data-testid="wb-import-warnings">
        {res.warnings.map((w, i) => <p key={i} className="text-xs">Row {w.row} ({w.slug}): {w.warning}</p>)}
      </div>
    )}
    {res.ready_for_redeploy && !res.published && <p className="mt-3 text-xs text-[#8A5A00]">New rows are in the <b>Needs review</b> tab. Approve them there to publish.</p>}
  </div>
);
